import { type ClassValue, clsx } from 'clsx'
import { FormatDistanceOptions, compareDesc, format, formatDistance, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { differenceBy } from 'lodash'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { twMerge } from 'tailwind-merge'
import colors from 'tailwindcss/colors'
import { z } from 'zod'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const BroadCastEvents = {
  newChapterAvailable: 'new-chapter-available',
} as const

export type BroadCastEvent = keyof typeof BroadCastEvents

export const parseDistanceByDate = (date: Date | string, options: FormatDistanceOptions = {}) => {
  const dateToParse = typeof date === 'string' ? parseISO(date) : date

  const now = new Date()

  return formatDistance(dateToParse, now, {
    addSuffix: true,
    includeSeconds: true,
    locale: ptBR,
    ...options,
  })
}

export const acceptFileTypes = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/blob',
  'image/gif',
  'image/avif',
]

export const validateFileType = (data: FileList | File) => {
  return acceptFileTypes.includes(data instanceof FileList ? data?.[0]?.type : data?.type)
}

export const getDefaultImageFile = async () => {
  const response = await fetch('/animes-default.jpg')

  const blob = await response.blob()

  return new File([blob], 'animes-default.jpg', { type: 'image/jpeg' })
}

export const isString = (value: unknown): value is string => typeof value === 'string'

export const isFileList = (value: unknown): value is FileList => value instanceof FileList

const mediaTypes = {
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)',
  'viewport-monitor': '(min-width: 1100px)',
} as const

export function useMediaQuery(media: keyof typeof mediaTypes) {
  const ac = new AbortController()
  const mediaQuery = window.matchMedia(mediaTypes[media])

  return useSyncExternalStore(
    (onStorageChange) => {
      mediaQuery.addEventListener('change', onStorageChange, {
        signal: ac.signal,
      })

      return () => {
        ac.abort()
      }
    },
    () => !mediaQuery.matches,
  )
}

export function parsePageQuery(value: unknown = '1') {
  return z.coerce
    .number()
    .transform((value) => (value > 0 ? value - 1 : 0))
    .parse(value)
}

export type ColorKey = keyof typeof colors

export function getAvailableTagColors() {
  const excludeColors = ['black', 'white', 'transparent', 'inherit', 'current']

  return differenceBy(Object.keys(colors), excludeColors) as [ColorKey]
}

export function useDebounceState<T>(initialValue: T, delay: number): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(initialValue)
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue)

  useEffect(() => {
    if (!value) {
      setDebouncedValue(value)
      return
    }

    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return [debouncedValue, setValue]
}

export const normalizeString = (value: string) => value.trim().normalize('NFC')

export function convertAndCompareDescendingDates<T extends string>(a: T, b: T) {
  return compareDesc(parseISO(a), parseISO(b))
}

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export type FindAnUpdateType<Entity> = {
  array: Entity[]
  identifyKey: keyof Entity
  identifierValue: Entity[keyof Entity]
  update: Partial<Entity>
}

export function findAndUpdate<T>({ update, identifyKey, identifierValue, array }: FindAnUpdateType<T>) {
  return array.map((item) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (item[identifyKey] === identifierValue) {
      return { ...item, ...update }
    }

    return item
  })
}

const colorOverrides = {
  warmGray: 'stone',
  lightBlue: 'sky',
}

export const isDeprecatedColorKey = (colorKey: string): colorKey is keyof typeof colorOverrides =>
  Object.keys(colorOverrides).includes(colorKey)

export const parseTagColor = (colorKey: string) => {
  let currentColor: (typeof colors)[keyof typeof colors]

  if (isDeprecatedColorKey(colorKey)) {
    const parsedColorKey = colorOverrides[colorKey]

    currentColor = colors[parsedColorKey as keyof typeof colors]
  } else {
    currentColor = colors[colorKey as keyof typeof colors] ?? colors.gray
  }

  return currentColor
}

type ColorShade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950

export const getTagColor = (color: ColorKey | string, colorNumber: ColorShade = 600) => {
  return parseTagColor(color)[colorNumber]
}

export const createDomainUrlWithPath = (path: string) => {
  return `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}${path}`
}

export const formatApiDate = (date: string | Date | undefined | null, schema?: string) => {
  const formatterSchema = schema ?? 'dddd/MM/yyyy '

  if (!date) return ''

  if (typeof date === 'string') {
    return format(new Date(date), formatterSchema)
  }

  return format(date, formatterSchema)
}

export const CACHE_TIME = {
  THIRTY_SECONDS: 30 * 1000,
  FIFTEEN_MINUTES: 15 * 60 * 1000,
  FIVE_MINUTES: 5 * 60 * 1000,
  ONE_HOUR: 60 * 60 * 1000,
  ONE_MINUTE: 60 * 1000,
  LIVE: 0,
  INFINITY: Infinity,
} as const
